/**
 * Fetches investment properties at a specific date using versioning
 * @param supabase - Supabase client instance
 * @param investmentSlug - Investment slug identifier
 * @param date - Target date for property data
 * @returns Array of properties with pricing data at the specified date
 */
export async function getInvestmentPropertiesAtDate(supabase, investmentSlug, date) {
    const { data, error } = await supabase.rpc('get_investment_properties_at_date', {
        p_investment_slug: investmentSlug,
        p_date: date.toISOString()
    });
    if (error) {
        throw error;
    }
    return data || [];
}
/**
 * Fetches additional costs for properties at a specific date using versioning
 * @param supabase - Supabase client instance
 * @param propertyIds - Array of property IDs
 * @param date - Target date for additional costs data
 * @returns Array of additional costs at the specified date
 */
export async function getAdditionalCostsAtDate(supabase, propertyIds, date) {
    const { data, error } = await supabase.rpc('get_additional_costs_at_date', {
        p_property_ids: propertyIds,
        p_date: date.toISOString()
    });
    if (error) {
        throw error;
    }
    return data || [];
}
/**
 * Toggles price badge visibility for a property.
 * Requires tenant module enabled + user authorization.
 */
export async function setPropertyPriceBadge(supabase, propertyId, isHidden) {
    const { data, error } = await supabase.rpc('set_property_price_badge', {
        p_property_id: propertyId,
        p_is_hidden: isHidden
    });
    if (error) {
        throw error;
    }
    return data;
}
/**
 * Lists custom promo badges for a tenant (read via RLS — public SELECT).
 */
export async function getTenantPromoBadges(supabase, tenantId) {
    const { data, error } = await supabase
        .from('tenant_promo_badges')
        .select('*')
        .eq('tenant_id', tenantId);
    if (error) {
        throw error;
    }
    return data ?? [];
}
/**
 * Creates a custom promo badge for a tenant. Requires module enabled + authorization.
 */
export async function createPromoBadge(supabase, params) {
    const { data, error } = await supabase.rpc('create_promo_badge', {
        p_tenant_id: params.tenantId,
        p_label: params.label,
        p_background_color: params.backgroundColor,
        p_text_color: params.textColor
    });
    if (error) {
        throw error;
    }
    return data;
}
/**
 * Updates a custom promo badge. NULL params keep existing values (COALESCE in RPC).
 */
export async function updatePromoBadge(supabase, params) {
    const { data, error } = await supabase.rpc('update_promo_badge', {
        p_badge_id: params.badgeId,
        p_label: params.label ?? undefined,
        p_background_color: params.backgroundColor ?? undefined,
        p_text_color: params.textColor ?? undefined
    });
    if (error) {
        throw error;
    }
    return data;
}
/**
 * Deletes a custom promo badge. FK ON DELETE SET NULL clears property assignments.
 */
export async function deletePromoBadge(supabase, badgeId) {
    const { data, error } = await supabase.rpc('delete_promo_badge', {
        p_badge_id: badgeId
    });
    if (error) {
        throw error;
    }
    return data;
}
/**
 * Assigns a promo badge to a property (NULL => default PROMOCJA).
 * Requires module enabled + authorization; badge must belong to the property's tenant.
 */
export async function setPropertyPromoBadge(supabase, propertyId, badgeId) {
    const { data, error } = await supabase.rpc('set_property_promo_badge', {
        p_property_id: propertyId,
        // RPC akceptuje NULL (uuid) = domyślny badge; wygenerowany typ jest zbyt restrykcyjny
        p_badge_id: badgeId
    });
    if (error) {
        throw error;
    }
    return data;
}
